import { NavLink } from 'react-router'
import type { JSX } from 'react'
import { initials } from '@/lib/format'
import { useActiveTenant, useTenants } from '@/tenant/context'
import { Lockup } from './Lockup'
import { MODULES, moduleById } from './navigation'
import type { ModuleId } from './navigation'

/**
 * The module switcher.
 *
 * Two buttons rather than a select, because it is a **mode** and both options
 * must be visible at a glance — S-D3's reasoning, and the reason it survives
 * the mobile collapse where ordinary navigation does not.
 */
export function ModuleSwitcher({
  active,
  onChange,
}: {
  active: ModuleId
  onChange: (id: ModuleId) => void
}): JSX.Element {
  return (
    <div className="app-switch" role="group" aria-label="Module">
      {MODULES.map((module) => (
        <button
          key={module.id}
          type="button"
          aria-pressed={module.id === active}
          onClick={() => {
            onChange(module.id)
          }}
        >
          {module.label}
        </button>
      ))}
    </div>
  )
}

/**
 * The section list for one module.
 *
 * `end` on the overview link: `/seo` is a prefix of every future `/seo/*`
 * route, and without it Overview would stay highlighted from inside Keywords.
 */
export function SectionNav({ module }: { module: ModuleId }): JSX.Element {
  const definition = moduleById(module)
  return (
    <nav className="app-nav" aria-label={definition.label}>
      {definition.sections.map((section) =>
        section.to ? (
          <NavLink key={section.label} to={section.to} end={section.to === definition.home}>
            {section.label}
          </NavLink>
        ) : (
          <span key={section.label} aria-disabled="true">
            {section.label}
            <span className="app-nav__soon">Soon</span>
          </span>
        ),
      )}
    </nav>
  )
}

/**
 * The whole sidebar: lockup, switcher, sections, and the organisation card.
 *
 * The card is a button that goes to `/organisations` rather than an inline
 * dropdown. Switching organisation is a *session* write on the server, not a
 * client-side filter — it can fail, and it needs somewhere to say so.
 */
export function Sidebar({
  module,
  onModuleChange,
  onNavigate,
  variant = 'fixed',
}: {
  module: ModuleId
  onModuleChange: (id: ModuleId) => void
  /** Called after any navigation — the mobile drawer closes itself with it. */
  onNavigate?: () => void
  variant?: 'fixed' | 'drawer'
}): JSX.Element {
  const { tenant, role } = useActiveTenant()
  const { memberships } = useTenants()

  return (
    <aside
      className={variant === 'drawer' ? 'app-side app-side--drawer' : 'app-side'}
      onClick={(event) => {
        // One handler rather than a callback threaded through every link: the
        // drawer should close on any navigation inside it, including links a
        // later session adds without knowing this exists.
        if (event.target instanceof HTMLElement && event.target.closest('a')) onNavigate?.()
      }}
    >
      <NavLink className="app-side__lockup" to={moduleById(module).home}>
        <Lockup />
      </NavLink>

      <ModuleSwitcher active={module} onChange={onModuleChange} />
      <SectionNav module={module} />

      <div className="app-side__foot">
        <NavLink className="app-tenant" to="/organisations">
          <span className="ar-avatar" aria-hidden="true">
            {initials(tenant.name)}
          </span>
          <span className="app-tenant__body">
            <strong>{tenant.name}</strong>
            <span>
              {role}
              {memberships.length > 1 ? ` · ${String(memberships.length)} organisations` : ''}
            </span>
          </span>
        </NavLink>
      </div>
    </aside>
  )
}
